import { useTranslation } from "react-i18next";

import {
  BuildingIcon,
  FactoryIcon,
  GlobeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
} from "lucide-react";

import { countries, regions } from "simplydo/core/countries";
import {
  accreditations,
  businessSizeOptions,
  businessStatusOptions,
  businessTypeOptions,
} from "simplydo/core/ideaBusinessProfiles";
import { ExternalLinkModal } from "simplydo/react/components/ExternalLinkModal";
import { styled } from "simplydo/react/styled";
import { Divider } from "simplydo/react/ui/divider";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Label } from "simplydo/react/ui/label";

import { TagView } from "../../Choosers/Tags/TagModalChooser";
import type { IdeaBusinessProfile } from "../types";
import {
  CompaniesHouseCheckCard,
  VatNumberCheckCard,
} from "./IdentifierCheckCards";
import { isCreditsafeVatVerified } from "./identifierLookup";

type OverviewProps = { businessProfile: IdeaBusinessProfile };

const AboutIcon = styled(Icon)`
  margin-right: 5px;
`;

export const Overview = ({ businessProfile }: OverviewProps) => {
  const { t } = useTranslation();
  const { address, ownerTags, foundingYear } = businessProfile || {};

  const { country, city, streetAddress, postcode, region } = address || {};
  const size = businessSizeOptions.find(
    (option) => option.value === businessProfile?.size,
  )?.text;
  const type = businessTypeOptions.find(
    (option) => option.value === businessProfile?.type,
  )?.text;
  const status = businessStatusOptions.find(
    (option) => option.value === businessProfile?.status,
  )?.text;
  const fullAddress = [
    streetAddress,
    city,
    countries.find((c) => c.iso2 === country)?.name,
    regions.find((r) => r.value === region)?.name,
    postcode,
  ].filter((addressSegment) => !!addressSegment);

  const showRegColumn =
    !!businessProfile?.creditsafeRegNoSkipped ||
    !!businessProfile?.creditsafeRegNo;
  const showVatColumn =
    !!businessProfile?.vatNoSkipped || !!businessProfile?.vatNo;

  const showCompaniesHouseCheck =
    !businessProfile?.creditsafeRegNoSkipped &&
    !!businessProfile?.creditsafeRegNo &&
    !!businessProfile?.companiesHouse?.isAvailable &&
    businessProfile.companiesHouse.companyNumber ===
      businessProfile.creditsafeRegNo;

  const showVatCheck = isCreditsafeVatVerified(businessProfile);

  return (
    <>
      <div style={{ display: "flex", gap: 24 }}>
        {businessProfile?.imageUrl && (
          <div
            style={{
              display: "flex",
              alignSelf: "flex-start",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 4,
              overflow: "hidden",
              height: 150,
              width: 150,
            }}
          >
            <Image
              src={businessProfile?.imageUrl}
              alt={businessProfile?.name}
            />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <H4 style={{ marginBottom: "0.3rem" }}>About</H4>
          {businessProfile?.websiteUrl && (
            <p style={{ marginBottom: 7 }}>
              <AboutIcon icon={GlobeIcon} />
              <ExternalLinkModal href={businessProfile.websiteUrl}>
                {businessProfile.websiteUrl}
              </ExternalLinkModal>
            </p>
          )}

          {fullAddress && fullAddress.length > 0 ? (
            <p style={{ marginBottom: 7 }}>
              <AboutIcon icon={MapPinIcon} />
              {fullAddress.join(", ")}
            </p>
          ) : null}

          {businessProfile?.phoneNumber && (
            <p style={{ marginBottom: 7 }}>
              <AboutIcon icon={PhoneIcon} />
              {businessProfile?.phoneNumber}
            </p>
          )}

          {size && (
            <p style={{ marginBottom: 7 }}>
              <AboutIcon icon={UsersIcon} />
              {size}
            </p>
          )}

          {(status || foundingYear) && (
            <p style={{ marginBottom: 7 }}>
              <AboutIcon icon={BuildingIcon} />
              {foundingYear ? `Founded: ${foundingYear}` : ""}
              {status ? `${foundingYear ? " | " : ""}${status}` : ""}
            </p>
          )}

          {type && (
            <p style={{ marginBottom: 7 }}>
              <AboutIcon icon={FactoryIcon} />
              {type}
            </p>
          )}
        </div>
      </div>
      <Divider hidden />
      {businessProfile?.accreditations &&
        businessProfile?.accreditations?.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 5,
              fontWeight: "bold",
            }}
          >
            <p style={{ marginBottom: 0, marginRight: 5 }}>Accreditations</p>
            {businessProfile?.accreditations.map((accreditation) => (
              <Label key={accreditation} size="small">
                {accreditations.find((acc) => acc?.key === accreditation)
                  ?.text || accreditation}
              </Label>
            ))}
          </div>
        )}
      {businessProfile?.tags && businessProfile?.tags?.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: 5,
            fontWeight: "bold",
            marginTop: "-8px",
          }}
        >
          <p style={{ marginBottom: 0, marginRight: 10 }}>Tags</p>
          <TagView tags={ownerTags || []} />
        </div>
      )}

      <p>{businessProfile?.description}</p>
      {showRegColumn || showVatColumn ? (
        <Grid columns={2} mobile={1} style={{ marginTop: 12 }}>
          <GridColumn>
            {showRegColumn ? (
              <>
                <p style={{ marginBottom: 6, fontWeight: "bold" }}>
                  Organisation registration number
                </p>
                {businessProfile.creditsafeRegNoSkipped ? (
                  <p style={{ margin: 0, color: "#9a9a9a" }}>
                    {t("ideaBusinessProfiles.identifiers.regNoSkipped")}
                  </p>
                ) : (
                  <>
                    <Label size="small">
                      {businessProfile.creditsafeRegNo}
                    </Label>
                    {showCompaniesHouseCheck ? (
                      <div style={{ marginTop: 10 }}>
                        <CompaniesHouseCheckCard
                          businessProfile={businessProfile}
                        />
                      </div>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </GridColumn>
          <GridColumn>
            {showVatColumn ? (
              <>
                <p style={{ marginBottom: 6, fontWeight: "bold" }}>
                  VAT number
                </p>
                {businessProfile.vatNoSkipped ? (
                  <p style={{ margin: 0, color: "#9a9a9a" }}>
                    {t("ideaBusinessProfiles.identifiers.vatNoSkipped")}
                  </p>
                ) : (
                  <>
                    <Label size="small">{businessProfile.vatNo}</Label>
                    {showVatCheck ? (
                      <div style={{ marginTop: 10 }}>
                        <VatNumberCheckCard businessProfile={businessProfile} />
                      </div>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </GridColumn>
        </Grid>
      ) : null}
      <Divider hidden />
    </>
  );
};
